import { createEffect, untrack } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import { sessionActor } from "@/lib/session-machine";
import { useQuery } from "@tanstack/solid-query";
import { queryKeys } from "@fieldcheck/shared";
import { cloneSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";

interface AppError {
  message?: string;
  status?: number;
}

function isAppError(error: unknown): error is AppError {
  return typeof error === "object" && error !== null && ("message" in error || "status" in error);
}

/**
 * Singleton component that synchronizes the TanStack Query session state
 * with the XState session actor.
 *
 * This MUST only be rendered once in the app (usually in AppProviders)
 * to prevent infinite reactivity loops.
 */
export function SessionBridge() {
  const snapshot = fromActorRef(sessionActor);

  const query = useQuery(() => ({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const result = await api.GET("/api/auth/get-session", {
        params: { query: { jwt: true } },
      });
      return result.data ?? null;
    },
    // Only fetch if the machine is done priming (local storage check)
    enabled: !untrack(() => snapshot().value === "priming"),
    staleTime: SESSION_STALE_TIME_MS,
  }));

  createEffect(() => {
    // 1. Only sync if the query has finished its first load
    if (!query.isPending) {
      // P23/I1: `SessionBridge` hangs from `AppProviders`, ABOVE the
      // router. Reading `query.data` without `isSuccess` suspends (it is the only
      // proxy field that does so) and takes down the entire app against the
      // `<Suspense fallback={null}>` in `__root`. `!isPending` is not enough: on
      // error it is also `false` and `data` still has no value.
      const sessionData = query.isSuccess && query.data ? cloneSession(query.data) : null;

      // 2. Prevent unnecessary updates using robust manual comparison.
      // Use untrack to avoid dependency on the machine state while reading it.
      const current = untrack(() => snapshot().context.session);
      const currentErr = untrack(() => {
        const err = snapshot().context.error;
        return isAppError(err) ? err : null;
      });

      const isDataSame =
        (!sessionData && !current) || (sessionData && sessionData.user.id === current?.user.id);

      const { error } = query;
      const appError = isAppError(error) ? error : null;
      const isErrorSame =
        (!appError && !currentErr) ||
        (appError?.message === currentErr?.message && appError?.status === currentErr?.status);

      if (isDataSame && isErrorSame) {
        return;
      }

      console.log("[SessionBridge] Syncing session to machine:", {
        hasData: Boolean(sessionData),
        hasError: Boolean(query.error),
      });

      sessionActor.send({
        type: "SERVER_REPLY",
        session: sessionData,
        error: query.error,
      });
    }
  });

  return null;
}
